import { ApiError, networkError, normalizeError } from './errors';
import type {
  AccountMe,
  ActionResult,
  ActiveRunStatusResponse,
  AdminConfigRecord,
  AdminConfigResponse,
  AdminOpsSummary,
  AdminAbuseResponse,
  AdminAbuseScanResponse,
  AbuseSignalRecord,
  BuyCosmeticListingResult,
  EligibilityRefresh,
  EligibilityStatus,
  EquipmentResponse,
  EquipSlot,
  CashoutOrdersResponse,
  CosmeticClaimCampaignResponse,
  CosmeticClaimCampaignStatus,
  CosmeticClaimCampaignsResponse,
  CosmeticFounderClaimResponse,
  CosmeticFounderClaimStatus,
  CosmeticListing,
  CosmeticListingsResponse,
  CosmeticShopResponse,
  MyCosmeticsResponse,
  MyCosmeticListingsResponse,
  ExtractionResult,
  GoldSaleOrder,
  GoldSalePurchaseIntent,
  MyGoldSalePurchaseIntentsResponse,
  HubEnterResponse,
  HubHeartbeatInput,
  HubHeartbeatResponse,
  HubInspectResponse,
  HubPresenceResponse,
  InventoryResponse,
  MarketListing,
  MarketListingsResponse,
  MarketPurchaseIntent,
  MarketTokenSettlement,
  MyMarketPurchaseIntentsResponse,
  MerchantBuySupplyResult,
  MerchantRepairItemResult,
  MerchantSalvageItemResult,
  MerchantStatus,
  MyListingsResponse,
  MyCashoutOrdersResponse,
  BuyListingResult,
  NonceResponse,
  PassStatus,
  RunState,
  RunReplayResult,
  StartRunResult,
  SubmitCashoutTxResult,
  SpinnerStatus,
  SpinnerOrder,
  SpinResult,
  SubmitSpinResult,
  SpectateRunsResponse,
  SpectateRunResponse,
  SpectateEventsResponse,
  SpectateWaystationPreview,
  SolanaPaymentContext,
  SolanaTokenBalance,
  SupplyKey,
  VerifyResponse,
} from './types';

/** A single run action, matching the backend zod `actionSchema` (runs/routes.ts). */
export type RunAction =
  | { type: 'move'; direction: 'up' | 'down' | 'left' | 'right' }
  | { type: 'attack'; targetId: string }
  | { type: 'use_consumable'; consumable: SupplyKey }
  | { type: 'equip_found'; itemId: string }
  | { type: 'pickup_item'; itemId: string }
  | { type: 'discard_item'; itemId: string }
  | { type: 'wait' }
  | { type: 'extract' }
  | { type: 'descend' };

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the current Bearer session token (or null when unauthenticated). */
  getToken?: () => string | null;
  /** Injectable fetch for tests; defaults to global fetch. */
  fetchFn?: typeof fetch;
}

export interface RequestOpts {
  /**
   * Single-use Cloudflare Turnstile token for protected actions, sent as the
   * `x-turnstile-token` header and verified server-side. Null/undefined when
   * Turnstile is not configured (dev/test) — the request is sent without it.
   */
  turnstileToken?: string | null;
}

/**
 * Typed client for the Gravelight API. Attaches the Bearer token, sends/receives
 * JSON, and throws an {@link ApiError} (normalized envelope) on any non-2xx — so
 * callers get a consistent, fully-surfaced error.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly fetchFn: typeof fetch;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.getToken = opts.getToken ?? (() => null);
    this.fetchFn = opts.fetchFn ?? ((...args) => fetch(...args));
  }

  async request<T>(method: string, path: string, body?: unknown, opts?: RequestOpts): Promise<T> {
    return this.requestWithBearer(method, path, body, this.getToken(), opts);
  }

  private async requestWithBearer<T>(
    method: string,
    path: string,
    body: unknown,
    bearerToken: string | null,
    opts?: RequestOpts,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (bearerToken) headers['authorization'] = `Bearer ${bearerToken}`;
    if (opts?.turnstileToken) headers['x-turnstile-token'] = opts.turnstileToken;

    let res: Response;
    try {
      res = await this.fetchFn(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new ApiError(networkError(err));
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const parsed = text.length > 0 ? safeJson(text) : undefined;
    if (!res.ok) {
      throw new ApiError(normalizeError(res.status, parsed ?? text));
    }
    return parsed as T;
  }

  private adminRequest<T>(adminToken: string, method: string, path: string, body?: unknown): Promise<T> {
    return this.requestWithBearer(method, path, body, adminToken);
  }

  // ── Auth ─────────────────────────────────────────────────────────────────
  nonce(wallet: string): Promise<NonceResponse> {
    return this.request('POST', '/auth/nonce', { wallet });
  }
  verify(input: { wallet: string; signature: string; nonce: string }): Promise<VerifyResponse> {
    return this.request('POST', '/auth/verify', input);
  }
  accountMe(): Promise<AccountMe> {
    return this.request('GET', '/account/me');
  }

  // ── Eligibility / Pass ─────────────────────────────────────────────────────
  eligibilityRefresh(turnstileToken?: string | null): Promise<EligibilityRefresh> {
    return this.request('POST', '/eligibility/refresh', {}, { turnstileToken });
  }
  eligibilityStatus(): Promise<EligibilityStatus> {
    return this.request('GET', '/eligibility/status');
  }
  passStatus(): Promise<PassStatus> {
    return this.request('GET', '/ferry-passes/status');
  }

  // ── Admin config (Phase L1) ────────────────────────────────────────────────
  adminConfig(adminToken: string): Promise<AdminConfigResponse> {
    return this.adminRequest(adminToken, 'GET', '/admin/config');
  }
  adminConfigKey(adminToken: string, key: string): Promise<AdminConfigRecord> {
    return this.adminRequest(adminToken, 'GET', `/admin/config/${encodeURIComponent(key)}`);
  }
  adminUpdateConfig(adminToken: string, key: string, value: unknown): Promise<AdminConfigRecord> {
    return this.adminRequest(adminToken, 'PUT', `/admin/config/${encodeURIComponent(key)}`, { value });
  }
  adminAbuse(adminToken: string): Promise<AdminAbuseResponse> {
    return this.adminRequest(adminToken, 'GET', '/admin/abuse');
  }
  adminAbuseSignal(adminToken: string, id: number): Promise<{ signal: AbuseSignalRecord }> {
    return this.adminRequest(adminToken, 'GET', `/admin/abuse/${encodeURIComponent(String(id))}`);
  }
  adminAbuseScan(adminToken: string): Promise<AdminAbuseScanResponse> {
    return this.adminRequest(adminToken, 'POST', '/admin/abuse/scan');
  }
  adminReviewAbuse(adminToken: string, id: number): Promise<{ signal: AbuseSignalRecord }> {
    return this.adminRequest(adminToken, 'POST', `/admin/abuse/${encodeURIComponent(String(id))}/review`);
  }
  adminDismissAbuse(adminToken: string, id: number): Promise<{ signal: AbuseSignalRecord }> {
    return this.adminRequest(adminToken, 'POST', `/admin/abuse/${encodeURIComponent(String(id))}/dismiss`);
  }
  adminFlagAbuse(adminToken: string, id: number): Promise<{ signal: AbuseSignalRecord }> {
    return this.adminRequest(adminToken, 'POST', `/admin/abuse/${encodeURIComponent(String(id))}/flag`);
  }
  adminReplayRun(adminToken: string, runId: string): Promise<RunReplayResult> {
    return this.adminRequest(adminToken, 'POST', `/admin/runs/${encodeURIComponent(runId)}/replay`);
  }
  adminOpsSummary(adminToken: string): Promise<AdminOpsSummary> {
    return this.adminRequest(adminToken, 'GET', '/admin/ops/summary');
  }

  // ── Runs ───────────────────────────────────────────────────────────────────
  startReward(loadout?: { extraOil?: number; extraTorch?: number; extraPotion?: number }): Promise<StartRunResult> {
    return this.request('POST', '/runs/reward/start', loadout ?? {});
  }
  startEcho(): Promise<StartRunResult> {
    return this.request('POST', '/runs/echo/start', {});
  }
  action(runId: string, action: RunAction): Promise<ActionResult> {
    return this.request('POST', `/runs/${encodeURIComponent(runId)}/action`, { action });
  }
  extract(runId: string): Promise<ExtractionResult> {
    return this.request('POST', `/runs/${encodeURIComponent(runId)}/extract`, {});
  }
  reconnect(runId: string): Promise<{ state: RunState }> {
    return this.request('POST', `/runs/${encodeURIComponent(runId)}/reconnect`, {});
  }
  abandon(runId: string): Promise<{ status: 'abandoned' }> {
    return this.request('POST', `/runs/${encodeURIComponent(runId)}/abandon`, {});
  }
  getRun(runId: string): Promise<{ status: string; state?: RunState; summary?: unknown }> {
    return this.request('GET', `/runs/${encodeURIComponent(runId)}`);
  }
  getActiveRun(): Promise<ActiveRunStatusResponse> {
    return this.request('GET', '/runs/active');
  }

  // ── Merchant supplies and repair ───────────────────────────────────────────
  merchantStatus(): Promise<MerchantStatus> {
    return this.request('GET', '/merchant/status');
  }
  merchantBuySupply(supplyKey: SupplyKey, quantity: number): Promise<MerchantBuySupplyResult> {
    return this.request('POST', '/merchant/buy-supply', { supplyKey, quantity });
  }
  merchantRepairItem(itemInstanceId: string): Promise<MerchantRepairItemResult> {
    return this.request('POST', '/merchant/repair-item', { itemInstanceId });
  }
  merchantSalvageItem(itemInstanceId: string): Promise<MerchantSalvageItemResult> {
    return this.request('POST', '/merchant/salvage-item', { itemInstanceId });
  }

  // ── Inventory / equipment ───────────────────────────────────────────────────
  inventory(): Promise<InventoryResponse> {
    return this.request('GET', '/inventory');
  }
  equipment(): Promise<EquipmentResponse> {
    return this.request('GET', '/equipment');
  }
  equip(itemInstanceId: string, slot: EquipSlot, confirmBind = false): Promise<{ equipped: true }> {
    return this.request('POST', '/equipment/equip', confirmBind
      ? { itemInstanceId, slot, confirmBind: true }
      : { itemInstanceId, slot });
  }
  unequip(slot: EquipSlot): Promise<{ unequipped: true }> {
    return this.request('POST', '/equipment/unequip', { slot });
  }

  // ── Waystation hub (Phase F) ─────────────────────────────────────────────────
  hubEnter(): Promise<HubEnterResponse> {
    return this.request('POST', '/hub/enter', {});
  }
  hubHeartbeat(input?: HubHeartbeatInput): Promise<HubHeartbeatResponse> {
    return this.request('POST', '/hub/heartbeat', input ?? {});
  }
  hubLeave(): Promise<{ left: true }> {
    return this.request('POST', '/hub/leave', {});
  }
  hubPresence(): Promise<HubPresenceResponse> {
    return this.request('GET', '/hub/presence');
  }
  hubInspect(accountId: string): Promise<HubInspectResponse> {
    return this.request('GET', `/hub/players/${encodeURIComponent(accountId)}/inspect`);
  }
  hubEmote(emote: string): Promise<{ emote: string; startedAt: string; expiresAt: string }> {
    return this.request('POST', '/hub/emote', { emote });
  }

  // ── Marketplace (Phase G) ─────────────────────────────────────────────────────
  marketplaceListings(): Promise<MarketListingsResponse> {
    return this.request('GET', '/marketplace/listings');
  }
  marketplaceMyListings(): Promise<MyListingsResponse> {
    return this.request('GET', '/marketplace/my-listings');
  }
  marketplaceListing(id: string): Promise<MarketListing> {
    return this.request('GET', `/marketplace/listings/${encodeURIComponent(id)}`);
  }
  marketplaceList(
    itemInstanceId: string,
    opts: { currency: 'gold'; price: number } | { currency: 'obol'; obolAmountTotal: string },
  ): Promise<MarketListing> {
    return this.request('POST', '/marketplace/listings', { itemInstanceId, ...opts });
  }
  marketplaceCreateIntent(listingId: string, turnstileToken?: string | null): Promise<MarketPurchaseIntent> {
    return this.request('POST', `/marketplace/listings/${encodeURIComponent(listingId)}/purchase-intents`, undefined, { turnstileToken });
  }
  cosmeticMarketplaceCreateIntent(listingId: string, turnstileToken?: string | null): Promise<MarketPurchaseIntent> {
    return this.request('POST', `/marketplace/cosmetic-listings/${encodeURIComponent(listingId)}/purchase-intents`, undefined, { turnstileToken });
  }
  marketplaceSubmitIntentTx(intentId: string, txSignature: string): Promise<MarketTokenSettlement> {
    return this.request('POST', `/marketplace/purchase-intents/${encodeURIComponent(intentId)}/submit-tx`, { txSignature });
  }
  marketplaceMyIntents(): Promise<MyMarketPurchaseIntentsResponse> {
    return this.request('GET', '/marketplace/purchase-intents/my');
  }
  marketplaceCancel(id: string): Promise<MarketListing> {
    return this.request('POST', `/marketplace/listings/${encodeURIComponent(id)}/cancel`);
  }
  marketplaceBuy(id: string): Promise<BuyListingResult> {
    return this.request('POST', `/marketplace/listings/${encodeURIComponent(id)}/buy`);
  }

  // ── Gold cash-out / Gold Exchange (Phase H) ─────────────────────────────────
  cashoutOrders(): Promise<CashoutOrdersResponse> {
    return this.request('GET', '/cashout/orders');
  }
  cashoutMyOrders(): Promise<MyCashoutOrdersResponse> {
    return this.request('GET', '/cashout/my-orders');
  }
  cashoutOrder(id: string): Promise<GoldSaleOrder> {
    return this.request('GET', `/cashout/orders/${encodeURIComponent(id)}`);
  }
  cashoutCreateOrder(goldAmount: number, obolAmount: number, expiryDays?: number, turnstileToken?: string | null): Promise<GoldSaleOrder> {
    return this.request('POST', '/cashout/orders', {
      goldAmount,
      obolAmount,
      ...(expiryDays !== undefined ? { expiryDays } : {}),
    }, { turnstileToken });
  }
  cashoutCancelOrder(id: string): Promise<GoldSaleOrder> {
    return this.request('POST', `/cashout/orders/${encodeURIComponent(id)}/cancel`);
  }
  /** Reserve an order for this buyer (audit1 PAY-1) — payment data lives ONLY on the intent. */
  cashoutCreateIntent(orderId: string, turnstileToken?: string | null): Promise<GoldSalePurchaseIntent> {
    return this.request('POST', `/cashout/orders/${encodeURIComponent(orderId)}/purchase-intents`, {}, { turnstileToken });
  }
  cashoutSubmitIntentTx(intentId: string, txSignature: string): Promise<SubmitCashoutTxResult> {
    return this.request('POST', `/cashout/purchase-intents/${encodeURIComponent(intentId)}/submit-tx`, { txSignature });
  }
  cashoutMyIntents(): Promise<MyGoldSalePurchaseIntentsResponse> {
    return this.request('GET', '/cashout/purchase-intents/my');
  }
  solanaPaymentContext(mint: string): Promise<SolanaPaymentContext> {
    return this.request('GET', `/solana/payment-context?mint=${encodeURIComponent(mint)}`);
  }
  solanaTokenBalance(mint: string, owner: string): Promise<SolanaTokenBalance> {
    return this.request('GET', `/solana/token-balance?mint=${encodeURIComponent(mint)}&owner=${encodeURIComponent(owner)}`);
  }

  // ── Spinner / Ferryman's Wager (Phase I) ────────────────────────────────────
  spinnerStatus(): Promise<SpinnerStatus> {
    return this.request('GET', '/spinner/status');
  }
  spinnerFreeSpin(): Promise<SpinResult> {
    return this.request('POST', '/spinner/free-spin');
  }
  spinnerCreateOrder(turnstileToken?: string | null): Promise<SpinnerOrder> {
    return this.request('POST', '/spinner/paid/orders', undefined, { turnstileToken });
  }
  spinnerOrder(id: string): Promise<SpinnerOrder> {
    return this.request('GET', `/spinner/paid/orders/${encodeURIComponent(id)}`);
  }
  spinnerCancelOrder(id: string): Promise<SpinnerOrder> {
    return this.request('POST', `/spinner/paid/orders/${encodeURIComponent(id)}/cancel`);
  }
  spinnerSubmitTx(id: string, txSignature: string): Promise<SubmitSpinResult> {
    return this.request('POST', `/spinner/paid/orders/${encodeURIComponent(id)}/submit-tx`, { txSignature });
  }

  // ── Spectate / non-holder funnel (Phase J) — public, no auth ─────────────────
  spectateRuns(): Promise<SpectateRunsResponse> {
    return this.request('GET', '/spectate/runs');
  }
  spectateRun(id: string): Promise<SpectateRunResponse> {
    return this.request('GET', `/spectate/runs/${encodeURIComponent(id)}`);
  }
  spectateEvents(id: string): Promise<SpectateEventsResponse> {
    return this.request('GET', `/spectate/runs/${encodeURIComponent(id)}/events`);
  }
  spectateWaystationPreview(): Promise<SpectateWaystationPreview> {
    return this.request('GET', '/spectate/waystation-preview');
  }

  // ── Cosmetics (Phase K) ────────────────────────────────────────────────────
  cosmeticsShop(): Promise<CosmeticShopResponse> {
    return this.request('GET', '/cosmetics/shop');
  }
  myCosmetics(): Promise<MyCosmeticsResponse> {
    return this.request('GET', '/cosmetics/my');
  }
  cosmeticBuy(cosmeticId: string): Promise<MyCosmeticsResponse> {
    return this.request('POST', '/cosmetics/buy', { cosmeticId });
  }
  cosmeticFounderClaimStatus(): Promise<CosmeticFounderClaimStatus> {
    return this.request('GET', '/cosmetics/founder-claim/status');
  }
  cosmeticFounderClaim(turnstileToken?: string | null): Promise<CosmeticFounderClaimResponse> {
    return this.request('POST', '/cosmetics/founder-claim/claim', {}, { turnstileToken });
  }
  cosmeticClaimCampaignStatuses(): Promise<CosmeticClaimCampaignsResponse> {
    return this.request('GET', '/cosmetics/claims/status');
  }
  cosmeticClaimCampaignStatus(campaignId: string): Promise<CosmeticClaimCampaignStatus> {
    return this.request('GET', `/cosmetics/claims/${encodeURIComponent(campaignId)}/status`);
  }
  cosmeticClaimCampaign(campaignId: string, turnstileToken?: string | null): Promise<CosmeticClaimCampaignResponse> {
    return this.request('POST', `/cosmetics/claims/${encodeURIComponent(campaignId)}/claim`, {}, { turnstileToken });
  }
  cosmeticEquip(cosmeticId: string): Promise<MyCosmeticsResponse> {
    return this.request('POST', '/cosmetics/equip', { cosmeticId });
  }
  cosmeticUnequip(cosmeticId: string): Promise<MyCosmeticsResponse> {
    return this.request('POST', '/cosmetics/unequip', { cosmeticId });
  }
  cosmeticListings(): Promise<CosmeticListingsResponse> {
    return this.request('GET', '/cosmetics/marketplace/listings');
  }
  myCosmeticListings(): Promise<MyCosmeticListingsResponse> {
    return this.request('GET', '/cosmetics/marketplace/my-listings');
  }
  cosmeticList(
    cosmeticId: string,
    opts: { currency: 'gold'; price: number } | { currency: 'obol'; obolAmountTotal: string },
  ): Promise<CosmeticListing> {
    return this.request('POST', '/cosmetics/marketplace/listings', { cosmeticId, ...opts });
  }
  cosmeticListingCancel(id: string): Promise<CosmeticListing> {
    return this.request('POST', `/cosmetics/marketplace/listings/${encodeURIComponent(id)}/cancel`);
  }
  cosmeticListingBuy(id: string): Promise<BuyCosmeticListingResult> {
    return this.request('POST', `/cosmetics/marketplace/listings/${encodeURIComponent(id)}/buy`);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
